<script lang="ts">
	/* eslint svelte/no-at-html-tags: "off" */
	import { browser } from '$app/environment';
	import { afterNavigate, beforeNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import { env } from '$env/dynamic/public';
	import BreadCrumb from '$lib/BreadCrumb.svelte';
	import Container from '$lib/Container.svelte';
	import Footer from '$lib/Footer.svelte';
	import Header from '$lib/Header/index.svelte';
	import LDTag from '$lib/LDTag.svelte';
	import Toaster from '$lib/Toaster.svelte';
	import { scrollProgressStore } from '$lib/stores/scrollProgress';
	import { userStore } from '$lib/stores/user';
	import { getBreadcrumbSchema, getDeviceCategory } from '$lib/utils';
	import { getUser } from '$lib/utils/auth';
	import { observeDOM, trackButton, trackLink } from '$lib/utils/mixpanel';
	import { QueryClient, QueryClientProvider } from '@tanstack/svelte-query';
	import dayjs from 'dayjs';
	import mixpanel from 'mixpanel-browser';
	import NProgress from 'nprogress';
	import { onMount } from 'svelte';
	import '../app.css';
	interface Props {
		children?: import('svelte').Snippet;
	}

	let { children }: Props = $props();

	beforeNavigate(() => {
		$scrollProgressStore = 0;
		NProgress.start();
	});

	afterNavigate(async ({ from }) => {
		$userStore = await getUser();
		mixpanel.register({
			context: page.data.pageContext ?? ''
		});

		document.querySelectorAll('a').forEach((el) => {
			el.removeEventListener('click', trackLink);
			el.addEventListener('click', trackLink);
		});

		document.querySelectorAll('button').forEach((el) => {
			el.removeEventListener('click', trackButton);
			el.addEventListener('click', trackButton);
		});

		observeDOM(document.body, (node) => {
			if (node.nodeName === 'A') {
				const el = node as HTMLAnchorElement;
				el.removeEventListener('click', trackLink);
				el.addEventListener('click', trackLink);
			}
			if (node.nodeName === 'BUTTON') {
				const el = node as HTMLButtonElement;
				el.removeEventListener('click', trackButton);
				el.addEventListener('click', trackButton);
			}
		});

		if (!page.url.pathname.startsWith('/embed')) {
			if (from?.url) {
				mixpanel.track_pageview({
					$referrer: from.url.toString(),
					$referring_domain: from.url.host
				});
			} else {
				mixpanel.track_pageview();
			}
		}

		NProgress.done();
	});

	onMount(() => {
		mixpanel.init(env.PUBLIC_MIXPANEL_TOKEN ?? '');
		mixpanel.register_once({
			device_category: getDeviceCategory()
		});
	});

	NProgress.configure({
		minimum: 0.16,
		showSpinner: false
	});

	let mainElement: HTMLElement | undefined = $state();
	let footerHeight: number = $state(0);

	$effect(() => {
		if (mainElement && footerHeight) {
			mainElement.style.paddingBottom = `${footerHeight}px`;
		}
	});

	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				enabled: browser
			}
		}
	});
</script>

<svelte:head>
	<title>
		{page.data.title ?? 'Invest and Grow with Beansprout '} - Growbeansprout.com
	</title>
	<meta name="description" content={page.data.description} />
	{#if page.data.noindex ?? page.error}
		<meta name="robots" content="noindex" />
	{:else}
		<link rel="canonical" href={`https://growbeansprout.com${page.url.pathname}`} />
	{/if}
	{#if page.data.opengraph}
		<meta property="og:title" content={page.data.opengraph.title} />
		<meta property="og:type" content={page.data.opengraph.type} />
		<meta property="og:url" content={page.data.opengraph.url} />
		<meta property="og:image" content={page.data.opengraph.image} />
	{/if}
	{#if page.data.breadcrumbs}
		<LDTag schema={getBreadcrumbSchema(page.data.breadcrumbs, page.url)} />
	{/if}
	{#each page.data.schemas ?? [] as schema}
		<LDTag {schema} />
	{/each}
	{#if page.url.pathname.startsWith('/tools') || page.url.pathname.startsWith('/quote')}
		<LDTag
			schema={{
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				name: page.data.title,
				description: page.data.description,
				dateModified: dayjs().toISOString(),
				datePublished: dayjs().toISOString(),
				publisher: {
					'@type': 'Organization',
					name: 'Beansprout',
					logo: {
						'@type': 'ImageObject',
						url: `${page.url.origin}/favicon.png`
					}
				}
			}}
		/>
	{/if}

	{#if env.PUBLIC_GTM_CONTAINER_ID}
		{@html `
		<script>
			(function (w, d, s, l, i) {
				w[l] = w[l] || [];
				w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
				var f = d.getElementsByTagName(s)[0],
					j = d.createElement(s),
					dl = l != 'dataLayer' ? '&l=' + l : '';
				j.async = true;
				j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
				f.parentNode.insertBefore(j, f);
			})(window, document, 'script', 'dataLayer', '${env.PUBLIC_GTM_CONTAINER_ID}');
		</${''}script>
		`}
	{/if}
</svelte:head>

{#if page.route.id?.match(/embed|newsletter-signup|course-landing/)}
	{@render children?.()}
{:else}
	<Toaster />
	<Header tools={page.data.tools} />

	<main bind:this={mainElement} class="relative flex flex-grow flex-col">
		{#if page.data.breadcrumbs}
			<Container wrapperClass="py-4 lg:py-8">
				<BreadCrumb breadcrumbs={page.data.breadcrumbs} />
			</Container>
		{/if}
		<QueryClientProvider client={queryClient}>
			{@render children?.()}
		</QueryClientProvider>
	</main>

	<Footer bind:footerHeight tools={page.data.tools} />
{/if}
